import { clients } from '@moovly/clients';
import {
  Library,
  LibraryResponse,
  LibraryShare,
  ShareLibraryType,
} from '@moovly/clients/lib/resources/libraries/libraries.interfaces';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  DefaultApiRequestState,
  getDefaultApiRequestState,
  getDefaultReducerForAsyncRequest,
  RequestType,
} from '../../../store/default';
import { toastInfo } from '../../../utils/ui';
import { t } from '@moovly/i18n';

const libResponseTransformer = (res: LibraryResponse) => res.results;

export const getLibraries = createAsyncThunk(
  'dashboard/libraries/getLibraries',
  ({ organizationId, groupIds }: { organizationId?: string; groupIds: string[] }) => {
    const fetchArray = [clients.Libraries.getUserPersonalLibraries('me').then(libResponseTransformer)];
    if (organizationId) {
      fetchArray.push(
        clients.Libraries.getExternalLibraries('organization', organizationId).then(libResponseTransformer)
      );
    }
    groupIds.forEach((groupId) => {
      fetchArray.push(clients.Libraries.getExternalLibraries('group', groupId).then(libResponseTransformer));
    });
    return Promise.all(fetchArray).then((data) =>
      data
        .flatMap((d) => d)
        .reduce((result: Library[], lib) => {
          if (!result.some((r) => r.id === lib.id)) {
            result.push(lib);
          }
          return result;
        }, [])
    );
  }
);

const createShares = async (externalIds: string[], libId: string, shareType: string) => {
  let shares: LibraryShare[] = [];
  for (let i = 0; i < externalIds.length; i++) {
    shares = await clients.Libraries.createLibraryShare(libId, {
      identityId: externalIds[i],
      identityType: shareType,
    }).then((s) => s.shares!);
  }
  return shares;
};

export const createLibrary = createAsyncThunk<
  Library,
  { shareType: ShareLibraryType; name: string; externalIds?: string[] }
>('dashboard/libraries/createLibrary', ({ name, shareType, externalIds }) => {
  return clients.Libraries.library.create(name).then(async (lib) => {
    if (shareType !== 'user') {
      lib.shares = await createShares(externalIds!, lib.id, shareType);
    }

    return lib;
  });
});
export const editLibrary = createAsyncThunk<
  Library,
  {
    library: Library;
    shareType: ShareLibraryType;
    name: string;
    externalIds?: string[];
  }
>('dashboard/libraries/editLibrary', ({ library, name, shareType, externalIds }) => {
  return clients.Libraries.library.update(library.id, { name }).then(async (lib) => {
    if (library.shares) {
      await Promise.all(library.shares.map((share) => clients.Libraries.deleteLibraryShare(library.id, share.guid)));
      lib.shares = undefined;
    }
    if (shareType !== 'user') {
      lib.shares = await createShares(externalIds!, lib.id, shareType);
    }
    return lib;
  });
});

export const deleteLibrary = createAsyncThunk<{ id: string }, { libraryId: string }>(
  'dashboard/libraries/deleteLibrary',
  async ({ libraryId }) => {
    await clients.Libraries.library.delete(libraryId);
    return { id: libraryId };
  }
);

type LibrariesState = {
  libraries: DefaultApiRequestState<
    Library[],
    [RequestType.Get, RequestType.Post, RequestType.Put, RequestType.Delete]
  >;
};

const initialState: LibrariesState = {
  libraries: getDefaultApiRequestState([RequestType.Get, RequestType.Post, RequestType.Put, RequestType.Delete]),
};

export const librariesSlice = createSlice({
  name: 'dashboard/libraries',
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: {
    ...getDefaultReducerForAsyncRequest({
      thunk: getLibraries,
      getInitialState: (state) => state.libraries,
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: createLibrary,
      requestType: RequestType.Post,
      getInitialState: (state) => state.libraries,
      onSuccess: (state, payload) => {
        toastInfo(t('libraries.alerts.created', { library: payload.name }));
        state.data = [...state.data, payload];
      },
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: editLibrary,
      requestType: RequestType.Put,
      getInitialState: (state) => state.libraries,
      onSuccess: (state, payload) => {
        toastInfo(t('libraries.alerts.updated', { library: payload.name }));

        state.data[state.data.findIndex((lib: Library) => lib.id === payload.id)] = payload;
      },
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: deleteLibrary,
      requestType: RequestType.Delete,
      getInitialState: (state) => state.libraries,
      onSuccess: (state, payload) => {
        toastInfo(t('libraries.alerts.deleted', { library: payload.name || 'Library' }));

        state.data = state.data.filter((lib: Library) => lib.id !== payload.id);
      },
    }),
  },
});

export default librariesSlice;
